/* MolCellQuest 問題データ — 発現制御・エピジェネティクス
   ★ 配列に追記すればそのまま反映されます（id は全体で一意に）。
   ★ diff : 80〜320。基礎 0-179 / 標準 180-269 / 応用 270+ */

const CAT = { track: "genome", category: "regulation", categoryName: "発現制御・エピジェネティクス" };

export const GENOME_REGULATION = [
  {
    id: "reg01", ...CAT,
    title: "プロモーターとエンハンサー", diff: 100, tags: ["プロモーター", "エンハンサー", "転写因子"],
    explain: `<p>真核生物の転写調節配列について、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "プロモーターは転写開始点の近くにあり、エンハンサーは遺伝子から数万塩基離れていても働く。",
      "エンハンサーは必ず遺伝子の上流にあり、下流には存在しない。",
      "プロモーターは向き（方向）を反転させても同じように機能する。",
      "エンハンサーはRNAポリメラーゼが直接結合する部位である。",
    ],
    answer: 0,
    hints: [
      "エンハンサーは距離にも向きにもしばられない、という性質が定義的な特徴です。",
      "DNAがループを作れば、遠く離れた配列もプロモーターに近づけます。",
    ],
    solution: `<p><b>【ステップ1】2つの配列の性質を対比する</b></p>
               <table>
                 <tr><th></th><th>プロモーター</th><th>エンハンサー</th></tr>
                 <tr><td>位置</td><td>転写開始点のすぐ近く</td><td><b>数万塩基離れていてもよい</b></td></tr>
                 <tr><td>上流/下流</td><td>基本的に上流</td><td><b>上流・下流・イントロン内、どこでも</b></td></tr>
                 <tr><td>向き</td><td>方向性がある（反転すると働かない）</td><td><b>反転しても働く</b></td></tr>
                 <tr><td>結合するもの</td><td>基本転写因子＋RNAポリメラーゼII</td><td>活性化因子（アクチベーター）</td></tr>
               </table>
               <p><b>【ステップ2】なぜ遠くから働けるのか</b></p>
               <p>DNAが<b>ループを作って折りたたまれる</b>ためです。
               エンハンサーに結合した活性化因子が、メディエーター複合体を介して
               プロモーター上の転写装置に物理的に接触します。
               ゲノムの三次元構造（TAD：トポロジー会合ドメイン）が、
               どのエンハンサーがどの遺伝子に働けるかを区切っています。</p>
               <p><b>【他の選択肢】</b></p>
               <ul>
                 <li>B ⇒ エンハンサーは下流やイントロン内にもあります。</li>
                 <li>C ⇒ 反転しても働くのは<b>エンハンサー</b>の性質です。</li>
                 <li>D ⇒ RNAポリメラーゼが集まるのはプロモーターです。</li>
               </ul>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg02", ...CAT,
    title: "ラクトースオペロンの基本", diff: 135, tags: ["lacオペロン", "リプレッサー", "負の制御"],
    explain: `<p>大腸菌のラクトースオペロンは、グルコースが無くラクトースがあるときに強く発現する。</p>
              <p>ラクトースが<b>存在しない</b>とき、オペロンで起きていることとして正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "リプレッサーがオペレーターに結合し、転写が抑えられている。",
      "リプレッサーが誘導物質に結合して外れ、転写が進んでいる。",
      "CAP-cAMP 複合体が結合して、転写が強く促進されている。",
      "オペロンの構造遺伝子が欠失している。",
    ],
    answer: 0,
    hints: [
      "ラクトースが無いとき、その代謝酵素を作る必要はありません。細胞はどうするでしょう。",
      "誘導物質（アロラクトース）が無ければ、リプレッサーは DNA に結合したままです。",
    ],
    solution: `<p><b>【ステップ1】lac オペロンの二重制御</b></p>
               <p>lac オペロンは、<b>負の制御</b>と<b>正の制御</b>の両方を受けます。</p>
               <ul>
                 <li><b>負の制御</b>：LacI リプレッサーがオペレーターに結合 → 転写を止める。
                     アロラクトース（誘導物質）が結合すると構造が変わり、DNAから外れる。</li>
                 <li><b>正の制御</b>：グルコースが少ないと cAMP が増え、CAP-cAMP がプロモーター上流に結合 →
                     RNAポリメラーゼを呼び込んで転写を強める。</li>
               </ul>
               <p><b>【ステップ2】4つの状況を整理する</b></p>
               <table>
                 <tr><th>グルコース</th><th>ラクトース</th><th>リプレッサー</th><th>CAP-cAMP</th><th>転写</th></tr>
                 <tr><td>あり</td><td>なし</td><td>結合（抑制）</td><td>結合せず</td><td>ほぼ無し</td></tr>
                 <tr><td>あり</td><td>あり</td><td>外れる</td><td>結合せず</td><td>わずか</td></tr>
                 <tr><td><b>なし</b></td><td>なし</td><td>結合（抑制）</td><td>結合</td><td>ほぼ無し</td></tr>
                 <tr><td><b>なし</b></td><td><b>あり</b></td><td>外れる</td><td>結合</td><td><b>最大</b></td></tr>
               </table>
               <p><b>【ステップ3】設問の状況</b></p>
               <p>ラクトースが無い＝誘導物質が無いので、リプレッサーはオペレーターに結合したまま、
               転写は抑えられています。必要のない酵素を作らないという、理にかなった仕組みです。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg03", ...CAT,
    title: "DNAメチル化と遺伝子発現", diff: 160, tags: ["DNAメチル化", "CpGアイランド", "エピジェネティクス"],
    explain: `<p>哺乳類のDNAメチル化について、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "アデニンの N6 位がメチル化される。",
      "プロモーター領域の CpG アイランドが高度にメチル化されると、その遺伝子は抑制されやすい。",
      "メチル化は塩基配列そのものを変えるため、変異の一種である。",
      "メチル化パターンは細胞分裂のたびに完全に失われる。",
    ],
    answer: 1,
    hints: [
      "哺乳類でメチル化されるのは、CpG 配列のどの塩基でしょうか。",
      "エピジェネティックな修飾は「配列を変えずに」発現を変えるものです。",
    ],
    solution: `<p><b>【ステップ1】哺乳類のDNAメチル化とは</b></p>
               <p><b>CpG 配列（5'-CG-3'）のシトシンの5位</b>がメチル化され、
               5-メチルシトシンになります（アデニンではありません）。</p>
               <p><b>【ステップ2】なぜ発現が抑えられるのか</b></p>
               <ul>
                 <li>メチル基が転写因子の結合を物理的に妨げる</li>
                 <li>メチル化CpG結合タンパク質（MeCP2 など）が結合し、
                     ヒストン脱アセチル化酵素を呼び込んでクロマチンを凝縮させる</li>
               </ul>
               <p>遺伝子のプロモーターには CpG が密集した <b>CpGアイランド</b> がよくあり、
               ここが高メチル化されると発現がしっかり抑えられます。
               がん細胞ではがん抑制遺伝子のCpGアイランドが異常にメチル化され、
               「変異なしに機能を失う」ことがよくあります。</p>
               <p><b>【ステップ3】他の選択肢を検討する</b></p>
               <ul>
                 <li>C ⇒ <b>配列は変わりません</b>。だからこそ「エピ（上の）ジェネティック」と呼ばれます。</li>
                 <li>D ⇒ 複製後、ヘミメチル化された CpG を <b>DNMT1</b> が認識して娘鎖にもメチル基を付けるので、
                     パターンは<b>維持されます</b>。これが「細胞の記憶」の実体です。</li>
               </ul>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "reg04", ...CAT,
    title: "ヒストン修飾の読み分け", diff: 175, tags: ["ヒストンアセチル化", "HAT", "HDAC"],
    explain: `<p>ヒストンのリジン残基のアセチル化が、一般に転写を<b>活性化</b>する理由として最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "アセチル化によりヒストンの正電荷が中和され、DNAとの静電的な結合がゆるむため。",
      "アセチル化によりヒストンが分解され、ヌクレオソームが消失するため。",
      "アセチル化がDNAの塩基配列を変え、転写因子の結合部位を作り出すため。",
      "アセチル化によりヒストンの負電荷が増し、DNAを引き寄せるため。",
    ],
    answer: 0,
    hints: [
      "リジンは側鎖に正電荷を持っています。アセチル基が付くとその電荷はどうなりますか。",
      "DNAは負に帯電しています。ヒストンの正電荷が減ると、両者の結合は強まるでしょうか、弱まるでしょうか。",
    ],
    solution: `<p><b>【ステップ1】電荷の変化を追う</b></p>
               <p>リジンの側鎖アミノ基（-NH<sub>3</sub><sup>+</sup>）は生理的pHで<b>正に帯電</b>しています。
               ここにアセチル基が付くとアミド（-NH-CO-CH<sub>3</sub>）になり、<b>正電荷が失われます</b>。</p>
               <p><b>【ステップ2】結合の強さへの影響</b></p>
               <p>DNAのリン酸骨格は<b>負電荷</b>。ヒストンの正電荷が減れば静電引力が弱まり、
               DNAがヒストンから少しほどけて<b>転写因子が近づきやすくなります</b>。</p>
               <p><b>【ステップ3】もうひとつの働き</b></p>
               <p>電荷の中和に加えて、アセチル化リジンは <b>ブロモドメイン</b> を持つタンパク質
               （転写共役因子やクロマチンリモデリング因子）の<b>結合目印</b>にもなります。
               「電荷を変える」と「読み手を呼ぶ」の二重の効果です。</p>
               <p><b>【ステップ4】酵素と創薬</b></p>
               <ul>
                 <li><b>HAT</b>（ヒストンアセチル基転移酵素）：アセチル基を付ける → 活性化</li>
                 <li><b>HDAC</b>（ヒストン脱アセチル化酵素）：外す → 抑制</li>
               </ul>
               <p>HDAC阻害剤が抗がん剤として使われるのは、
               抑制されたがん抑制遺伝子を再び発現させることを狙ったものです。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg05", ...CAT,
    title: "X染色体の不活性化", diff: 215, tags: ["Xist", "バー小体", "モザイク"],
    explain: `<p>哺乳類の雌では、2本のX染色体のうち1本が不活性化される。</p>
              <p>この現象について<b>誤っている</b>記述はどれか。</p>`,
    type: "radio",
    options: [
      "不活性化されたX染色体は凝縮し、バー小体として観察される。",
      "不活性化には Xist という長鎖ノンコーディングRNAが関わる。",
      "どちらの親由来のX染色体が不活性化されるかは、細胞ごとにランダムに決まる（一部の例外を除く）。",
      "いったん不活性化されたX染色体は、その細胞が分裂すると毎回どちらを不活性化するか選び直される。",
    ],
    answer: 3,
    hints: [
      "三毛猫の毛色がまだら模様になる理由を考えてみましょう。分裂のたびに選び直されたらどうなりますか。",
      "エピジェネティックな状態は、娘細胞に「受け継がれる」のが特徴です。",
    ],
    solution: `<p><b>【ステップ1】X染色体不活性化のしくみ</b></p>
               <ul>
                 <li>不活性化されるX染色体から <b>Xist</b>（長鎖ノンコーディングRNA）が発現する</li>
                 <li>Xist RNA が自分を産んだX染色体を<b>シスに覆っていく</b></li>
                 <li>ポリコーム複合体などが呼び込まれ、H3K27me3・DNAメチル化が蓄積して凝縮</li>
                 <li>凝縮した染色体は光学顕微鏡で <b>バー小体</b>として見える</li>
               </ul>
               <p><b>【ステップ2】選択はランダム、しかし記憶される</b></p>
               <p>どちらを不活性化するかは<b>発生初期に細胞ごとにランダムに1回だけ</b>決まり、
               その後は<b>その細胞の子孫すべてに安定に受け継がれます</b>。
               分裂のたびに選び直されることはありません。</p>
               <p><b>【ステップ3】三毛猫が証拠になる</b></p>
               <p>毛色の遺伝子はX染色体上にあります。もし分裂のたびに選び直されるなら、
               毛色は細かく混ざって一様な色に見えるはずです。
               実際には<b>はっきりした色のパッチ（斑）</b>になります。
               これは「1個の細胞が決めた選択が、その細胞から増えたクローン全体に維持された」ことの
               目に見える証拠です。<br>
               なお三毛猫がほぼ雌だけなのも、X染色体を2本持つ必要があるためです。</p>
               <p>したがって、誤っているのは <b>D</b> です。</p>`,
  },

  {
    id: "reg06", ...CAT,
    title: "miRNA による発現制御", diff: 240, tags: ["miRNA", "RISC", "RNA干渉"],
    explain: `<p>マイクロRNA（miRNA）による遺伝子発現制御について、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "miRNA は標的mRNAと完全に相補的に結合し、必ず切断する。",
      "miRNA は Argonaute を含む RISC に取り込まれ、主に標的mRNAの 3'UTR に部分的相補性で結合して翻訳を抑える。",
      "miRNA はタンパク質に直接結合してその活性を阻害する。",
      "1つの miRNA が制御できる標的遺伝子は原則1つだけである。",
    ],
    answer: 1,
    hints: [
      "動物の miRNA が標的と結合するとき、完全に一致しているでしょうか。「シード配列」という言葉を思い出しましょう。",
      "部分的にしか合っていないからこそ、1つの miRNA が多くの標的を持てます。",
    ],
    solution: `<p><b>【ステップ1】miRNA の成熟と作用</b></p>
               <ol>
                 <li>pri-miRNA として転写される</li>
                 <li>核内で <b>Drosha</b> が切って pre-miRNA に</li>
                 <li>細胞質へ運ばれ <b>Dicer</b> が切って約22塩基の二本鎖に</li>
                 <li>一方の鎖が <b>Argonaute</b> を含む <b>RISC</b> に取り込まれる</li>
                 <li>標的mRNAの主に <b>3'UTR</b> に結合し、翻訳抑制・脱アデニル化・分解を引き起こす</li>
               </ol>
               <p><b>【ステップ2】部分的相補性という要点</b></p>
               <p>動物の miRNA は標的と<b>部分的にしか相補的ではありません</b>。
               特に重要なのが 5' 側の 2〜8番目の <b>シード配列</b>で、ここさえ合えば結合できます。</p>
               <p>この「ゆるさ」の帰結が重要です。わずか8塩基の一致でよいので、
               <b>1つの miRNA が数百の遺伝子を制御でき</b>、逆に1つの遺伝子が
               複数の miRNA に制御されます。ヒトの遺伝子の6割以上が
               miRNA の標的になると見積もられています。</p>
               <p><b>【ステップ3】siRNA との対比</b></p>
               <table>
                 <tr><th></th><th>miRNA</th><th>siRNA</th></tr>
                 <tr><td>由来</td><td>内在性（ゲノムにコード）</td><td>外来性・人工が多い</td></tr>
                 <tr><td>相補性</td><td><b>部分的</b>（シード配列）</td><td><b>完全</b></td></tr>
                 <tr><td>主な結果</td><td>翻訳抑制・分解</td><td>標的の切断</td></tr>
                 <tr><td>標的数</td><td>多数</td><td>原則1つ</td></tr>
               </table>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "reg07", ...CAT,
    title: "トリプトファンオペロンとアテニュエーション", diff: 265, tags: ["trpオペロン", "アテニュエーション", "共役"],
    explain: `<p>大腸菌のトリプトファンオペロンは、トリプトファンが豊富なときに発現が抑えられる。</p>
              <p>リプレッサーによる制御に加えて働く「アテニュエーション（減衰）」の仕組みとして、
              正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "トリプトファンが豊富だとリボソームが前駆ペプチドを速く翻訳し、mRNAにターミネーター構造ができて転写が途中で終わる。",
      "トリプトファンが豊富だとリボソームが停止し、アンチターミネーター構造ができて転写が続く。",
      "トリプトファンが豊富だとDNAがメチル化され、転写が止まる。",
      "トリプトファンが豊富だと tRNA<sup>Trp</sup> が枯渇し、翻訳が止まる。",
    ],
    answer: 0,
    hints: [
      "前駆ペプチドにはトリプトファンのコドンが連続して含まれています。Trp が豊富ならリボソームはそこを速く通過できます。",
      "リボソームの位置が、mRNA がどのヘアピン構造を取るかを決めています。",
    ],
    solution: `<p><b>【ステップ1】アテニュエーションが成り立つ前提</b></p>
               <p>原核生物では<b>転写と翻訳が同じ場所で同時に進みます</b>。
               だから「リボソームがどこにいるか」で「転写を続けるかどうか」を決められます。
               真核生物にこの仕組みが無いのは、転写と翻訳が核と細胞質に分かれているからです。</p>
               <p><b>【ステップ2】リーダー領域の構造</b></p>
               <p>trp オペロンの前方（リーダー領域）には、
               <b>Trp コドンが2個連続する短い前駆ペプチド</b>がコードされており、
               その下流には 1・2・3・4 と番号のついた4つの相補的な領域があります。</p>
               <ul>
                 <li><b>3-4 の対合</b> → ターミネーター（ヘアピン＋ポリU）→ 転写が<b>途中で終わる</b></li>
                 <li><b>2-3 の対合</b> → アンチターミネーター → 転写が<b>続く</b></li>
               </ul>
               <p><b>【ステップ3】トリプトファン量で場合分けする</b></p>
               <table>
                 <tr><th>Trp</th><th>リボソームの挙動</th><th>できる構造</th><th>結果</th></tr>
                 <tr><td><b>豊富</b></td><td>Trp コドンを<b>速く</b>通過し、領域2を覆う</td><td>3-4（ターミネーター）</td><td><b>転写が終了</b></td></tr>
                 <tr><td><b>不足</b></td><td>Trp コドンで<b>停止</b>し、領域1で止まる</td><td>2-3（アンチターミネーター）</td><td>転写が継続</td></tr>
               </table>
               <p><b>【ステップ4】意義</b></p>
               <p>リプレッサーによる制御が「大まかなスイッチ」だとすれば、
               アテニュエーションは<b>細胞内の Trp 濃度を直接センサーする微調整</b>です。
               2つを組み合わせることで、発現量を最大で 600 倍以上も変化させられます。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg08", ...CAT,
    title: "ゲノムインプリンティング", diff: 280, tags: ["インプリンティング", "刷り込み", "疾患"],
    explain: `<p>ヒトの15番染色体長腕の同じ領域（15q11-q13）の欠失が、
              父由来染色体で起こるとプラダー・ウィリー症候群、
              母由来染色体で起こるとアンジェルマン症候群という<b>別の疾患</b>になる。</p>
              <p>この現象を説明する最も適切な機構はどれか。</p>`,
    type: "radio",
    options: [
      "欠失の大きさが父由来と母由来で異なるため。",
      "その領域には親のどちらに由来するかで発現が決まる（片親性発現の）遺伝子があり、機能する側が失われると症状が出るため。",
      "父由来染色体のほうが変異率が高いため。",
      "X染色体不活性化により、片方の染色体が常に不活性化されるため。",
    ],
    answer: 1,
    hints: [
      "常染色体は2本あるのに、なぜ片方が欠けるだけで発症するのでしょう。もう片方は働いていないのでしょうか。",
      "「もともと片方の親由来からしか発現していない遺伝子」があると考えると説明がつきます。",
    ],
    solution: `<p><b>【ステップ1】ふつうの常染色体劣性との違い</b></p>
               <p>常染色体は父母から1本ずつ、計2本あります。ふつうなら片方が欠けても
               もう片方が働くので、症状は出にくいはずです。
               ところがこの領域では、<b>片方の親由来のコピーがもともと発現していない</b>——
               つまり実質的に1コピーしか働いていないのです。</p>
               <p><b>【ステップ2】インプリンティングの実体</b></p>
               <p>配偶子形成の過程で、特定の領域に<b>親の性に応じた DNA メチル化</b>が刻まれます。
               この「刷り込み」は受精後の初期化を免れて維持され、
               その遺伝子を父由来だけ、あるいは母由来だけ発現させます。</p>
               <table>
                 <tr><th>疾患</th><th>欠失した側</th><th>失われる働き</th><th>主な特徴</th></tr>
                 <tr><td>プラダー・ウィリー症候群</td><td><b>父</b>由来</td><td>父由来発現遺伝子（SNRPN など）</td><td>筋緊張低下、過食、肥満</td></tr>
                 <tr><td>アンジェルマン症候群</td><td><b>母</b>由来</td><td>母由来発現遺伝子 <i>UBE3A</i></td><td>重度知的障害、失調性歩行、多幸的な笑い</td></tr>
               </table>
               <p><b>【ステップ3】なぜ片親性発現になったのか</b></p>
               <p>有力な説が<b>親の対立仮説</b>です。父由来の遺伝子は「胎児をより大きく育てる」方向に、
               母由来の遺伝子は「母体の資源を配分する」方向に利害が食い違うため、
               片方だけを発現させる仕組みが進化したと考えられています。</p>
               <p><b>【ステップ4】他の選択肢を退ける</b></p>
               <ul>
                 <li>A・C ⇒ 同じ領域の同程度の欠失でも、由来する親によって症状が変わることの説明になりません。</li>
                 <li>D ⇒ X染色体不活性化はX染色体の現象で、15番染色体には関係しません。
                     またX不活性化は<b>ランダム</b>で、インプリンティングのように親の由来では決まりません。</li>
               </ul>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "reg09", ...CAT,
    title: "レポーターアッセイの結果を読む", diff: 235, tags: ["レポーターアッセイ", "実験推論", "エンハンサー"],
    explain: `<p>ある遺伝子の上流 2000 塩基を段階的に削り、
              ルシフェラーゼ遺伝子につないで培養細胞に導入し、発光量を測った。</p>
              <table>
                <tr><th>導入した上流領域</th><th>相対発光量</th></tr>
                <tr><td>-2000 〜 +1</td><td>100</td></tr>
                <tr><td>-1200 〜 +1</td><td>98</td></tr>
                <tr><td>-800 〜 +1</td><td>25</td></tr>
                <tr><td>-300 〜 +1</td><td>22</td></tr>
                <tr><td>-50 〜 +1</td><td>2</td></tr>
              </table>
              <p>この結果から言えることとして、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "-2000 〜 -1200 の領域に、強い転写活性化配列がある。",
      "-1200 〜 -800 の領域に、強い転写活性化配列がある。",
      "-800 〜 -300 の領域に、転写を抑制する配列がある。",
      "-300 〜 -50 の領域に、強い転写活性化配列がある。",
    ],
    answer: 1,
    hints: [
      "発光量が大きく落ちたのはどの段階でしょうか。削られた区間に何かがあったということです。",
      "100 → 98 はほぼ変化なし、98 → 25 は大きく低下しています。",
    ],
    solution: `<p><b>【ステップ1】隣り合う条件の差を見る</b></p>
               <p>欠失実験では、<b>削った区間</b>と<b>活性の変化</b>を対応づけます。</p>
               <table>
                 <tr><th>削られた区間</th><th>活性の変化</th><th>解釈</th></tr>
                 <tr><td>-2000 〜 -1200</td><td>100 → 98（ほぼ不変）</td><td>重要な配列は無い</td></tr>
                 <tr><td><b>-1200 〜 -800</b></td><td><b>98 → 25（大幅低下）</b></td><td><b>強い活性化配列がある</b></td></tr>
                 <tr><td>-800 〜 -300</td><td>25 → 22（ほぼ不変）</td><td>重要な配列は無い</td></tr>
                 <tr><td>-300 〜 -50</td><td>22 → 2（低下）</td><td>基本プロモーター領域</td></tr>
               </table>
               <p><b>【ステップ2】最大の落差を特定する</b></p>
               <p>98 → 25 の低下がいちばん大きく、活性の約 4分の3 が失われています。
               したがって <b>-1200 〜 -800</b> にエンハンサー様の活性化配列があると考えられます。</p>
               <p><b>【ステップ3】他の選択肢を検討する</b></p>
               <ul>
                 <li>A ⇒ この区間を削っても活性はほぼ変わりません（100→98）。</li>
                 <li>C ⇒ <b>抑制</b>配列があるなら、削ったときに活性が<b>上がる</b>はずです。実際は 25→22 とほぼ不変です。</li>
                 <li>D ⇒ 22 → 2 の低下は基本プロモーターの喪失によるもので、
                     「強い活性化配列」というより転写そのものの土台です。
                     -50 でもわずかに発光していることに注意。</li>
               </ul>
               <p class="tiny">読み方のコツ：欠失で<b>下がれば活性化配列</b>、<b>上がれば抑制配列</b>。
               絶対値ではなく、隣接する条件どうしの差を見ます。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "reg10", ...CAT,
    title: "山中因子と細胞の運命", diff: 300, tags: ["iPS細胞", "転写因子", "エピジェネティック初期化"],
    explain: `<p>体細胞に4つの転写因子（Oct3/4, Sox2, Klf4, c-Myc）を導入すると、
              多能性幹細胞（iPS細胞）になる。</p>
              <p>この事実が示すこととして、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "分化した細胞では、多能性に関わる遺伝子が不可逆的に失われている。",
      "分化した細胞もゲノムの内容は同じで、エピジェネティックな状態を書き換えれば運命を戻せる。",
      "4因子が細胞のゲノムに新しい多能性遺伝子を挿入している。",
      "細胞の分化は細胞質の因子だけで決まり、核は関与しない。",
    ],
    answer: 1,
    hints: [
      "分化した細胞と受精卵は、ゲノムの塩基配列が違うのでしょうか、同じなのでしょうか。",
      "4因子は「転写因子」です。遺伝子を新しく持ち込むのではなく、既にある遺伝子に何をしていますか。",
    ],
    solution: `<p><b>【ステップ1】前提を確認する</b></p>
               <p>多細胞生物のほぼすべての体細胞は、<b>同じゲノム配列</b>を持っています。
               皮膚細胞と神経細胞の違いは、配列ではなく
               <b>どの遺伝子が読まれているか</b>——すなわちエピジェネティックな状態の違いです。</p>
               <p><b>【ステップ2】4因子は何をしているか</b></p>
               <p>Oct3/4・Sox2・Klf4・c-Myc はいずれも<b>転写因子</b>で、
               新しい遺伝子を持ち込むわけではありません。
               すでにゲノムにあるのに<b>抑制されていた</b>多能性遺伝子群のクロマチンを開き、
               同時に体細胞としての遺伝子発現プログラムを解除します。
               DNAメチル化やヒストン修飾のパターンが初期化され、細胞は多能性状態に戻ります。</p>
               <p><b>【ステップ3】先行する実験とのつながり</b></p>
               <p>この結論はガードンのカエルの<b>核移植</b>実験（1962年）で既に示唆されていました。
               分化したオタマジャクシの腸細胞の核を除核卵に移すと、正常な個体が生まれたのです。
               「分化した核も全能性の情報を保っている」ことが分かっていた。<br>
               山中の仕事は、それを<b>卵の細胞質に頼らず、たった4つの定義された因子で</b>
               実現できると示した点が画期的でした。この2つの仕事が2012年のノーベル賞を共有しています。</p>
               <p><b>【ステップ4】他の選択肢を退ける</b></p>
               <ul>
                 <li>A ⇒ 「不可逆」ではないことこそが、この実験の示したことです。</li>
                 <li>C ⇒ 導入するのは転写因子であって、多能性遺伝子そのものではありません。
                     現在ではベクターを残さない方法（mRNA、エピソーマル、センダイウイルス）が主流です。</li>
                 <li>D ⇒ 核の状態が書き換えられることが要点で、核は中心的に関与しています。</li>
               </ul>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "reg11", ...CAT,
    title: "H3K4me3 と H3K9me3 ─ 修飾が示す意味", diff: 150, tags: ["ヒストン修飾", "H3K4me3", "H3K9me3"],
    explain: `<p>ヒストンH3のリシン残基のメチル化について、遺伝子が<b>活発に転写されている</b>領域に多く見られる修飾はどれか。</p>`,
    type: "radio",
    options: [
      "H3K4me3（H3の4番目のリシンのトリメチル化）",
      "H3K9me3（H3の9番目のリシンのトリメチル化）",
      "H3K27me3（H3の27番目のリシンのトリメチル化）",
      "H4K20me3（H4の20番目のリシンのトリメチル化）",
    ],
    answer: 0,
    hints: [
      "同じ「メチル化」でも、どの残基かで意味が正反対になります。",
      "K4 は転写開始点まわり、K9 はヘテロクロマチンを思い浮かべてください。",
    ],
    solution: `<p><b>【ステップ1】メチル化＝活性化ではない</b></p>
               <p>アセチル化は「ほぼ常に活性化」ですが、<b>メチル化は残基によって意味が逆転</b>します。
               ここが試験で最も狙われるところです。</p>
               <table>
                 <tr><th>修飾</th><th>意味</th><th>どこにあるか</th></tr>
                 <tr><td><b>H3K4me3</b></td><td><b>活性</b></td><td>活発な遺伝子の転写開始点近傍</td></tr>
                 <tr><td>H3K4me1 + H3K27ac</td><td>活性</td><td>活性型エンハンサー</td></tr>
                 <tr><td><b>H3K9me2/me3</b></td><td><b>不活性</b></td><td>構成的ヘテロクロマチン（HP1が結合）</td></tr>
                 <tr><td>H3K27me3</td><td>不活性</td><td>ポリコームによる抑制領域（通性ヘテロクロマチン）</td></tr>
                 <tr><td>H4K20me3</td><td>不活性</td><td>ヘテロクロマチン</td></tr>
               </table>
               <p><b>【ステップ2】覚え方</b></p>
               <p><b>K4 だけが活性</b>、K9・K27・K20 は抑制、と押さえるのが最短です。
               「4 は良い（good）、9 は閉じる」と語呂で覚えてもかまいません。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg12", ...CAT,
    title: "ヒストンのメチル化 ─ リシンとアルギニンの違い", diff: 195, tags: ["ヒストン修飾", "メチル化", "アルギニン"],
    explain: `<p>ヒストンのメチル化について、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "リシンは最大3個、アルギニンは最大2個のメチル基を受け取り、アルギニンのジメチル化には対称型と非対称型がある。",
      "リシンは最大2個、アルギニンは最大3個のメチル基を受け取る。",
      "リシンもアルギニンも最大1個のメチル基しか受け取れない。",
      "メチル化されるとヒストンの正電荷が失われ、DNAとの結合が弱まる。",
    ],
    answer: 0,
    hints: [
      "側鎖の窒素の数を数えてみてください。リシンのアミノ基は1つ、アルギニンのグアニジノ基は複数の窒素を持ちます。",
      "アセチル化と違い、メチル化では電荷が変わりません。",
    ],
    solution: `<p><b>【ステップ1】いくつメチル基が付くか</b></p>
               <table>
                 <tr><th>残基</th><th>最大メチル基数</th><th>形</th></tr>
                 <tr><td><b>リシン (K)</b></td><td><b>3</b></td><td>me1 / me2 / me3</td></tr>
                 <tr><td><b>アルギニン (R)</b></td><td><b>2</b></td><td>me1 / <b>対称型ジメチル化</b> / <b>非対称型ジメチル化</b></td></tr>
               </table>
               <p>アルギニンは2つのメチル基が同じ窒素に付くか（非対称）、別々の窒素に付くか（対称）で
               2通りのジメチル化があります。<b>「アルギニンは2まで、ただし2の付き方が2種類」</b>が答えの核心です。</p>
               <p><b>【ステップ2】電荷は変わらない</b></p>
               <p>D は<b>アセチル化</b>の説明です。アセチル化はリシンの正電荷を打ち消して
               DNA（負電荷）との静電的な結合をゆるめますが、<b>メチル化では正電荷が保たれます</b>。
               メチル化は「電荷を変える」のではなく「読み取りタンパク質の目印になる」修飾です。</p>
               <p><b>【ステップ3】メチル基はどこから来るか</b></p>
               <p>供与体は <b>SAM（S-アデノシルメチオニン）</b>です。
               アセチル基は<b>アセチルCoA</b>、リン酸は ATP、ADPリボースは NAD⁺ から来ます。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg13", ...CAT,
    title: "維持メチル化と新規メチル化", diff: 215, tags: ["DNAメチル化", "DNMT1", "UHRF1", "DNMT3"],
    explain: `<p>DNA複製の直後、親鎖だけがメチル化された<b>ヘミメチル化</b>のCpG部位ができる。</p>
              <p>この部位を認識して娘鎖にもメチル化を入れ、メチル化パターンを娘細胞へ受け継がせる仕組みとして正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "UHRF1 がヘミメチル化CpGを認識し、DNMT1 が娘鎖をメチル化する。",
      "DNMT3L がヘミメチル化CpGを認識し、DNMT3A が娘鎖をメチル化する。",
      "TET がヘミメチル化CpGを認識し、DNMT1 が娘鎖をメチル化する。",
      "MBD タンパク質がヘミメチル化CpGを切断し、修復の過程でメチル化が入る。",
    ],
    answer: 0,
    hints: [
      "「維持」の担当と「新規」の担当は、酵素も補助因子も別です。",
      "DNMT1 は単独ではヘミメチル化部位を見つけられません。案内役が必要です。",
    ],
    solution: `<p><b>【ステップ1】維持と新規を分けて覚える</b></p>
               <table>
                 <tr><th></th><th>維持メチル化</th><th>新規（de novo）メチル化</th></tr>
                 <tr><td>酵素</td><td><b>DNMT1</b></td><td><b>DNMT3A / DNMT3B</b></td></tr>
                 <tr><td>補助因子</td><td><b>UHRF1</b>（ヘミメチル化CpGを認識）</td><td><b>DNMT3L</b>（触媒活性なし）</td></tr>
                 <tr><td>基質</td><td>ヘミメチル化CpG</td><td>非メチル化CpG</td></tr>
                 <tr><td>いつ</td><td>複製直後（S期）</td><td>初期発生・生殖細胞</td></tr>
                 <tr><td>意義</td><td>パターンの<b>継承</b></td><td>パターンの<b>新設</b></td></tr>
               </table>
               <p><b>【ステップ2】DNMT3L が何をしているか</b></p>
               <p>DNMT3L 自身は酵素活性を持ちません。<b>修飾されていないヒストンH3テール（H3K4が非メチル化）</b>と
               相互作用し、DNMT3A/3B を呼び込んで新規メチル化を促進します。
               裏を返すと、<b>H3K4がメチル化されている領域には新規メチル化が入りにくい</b>ということです。</p>
               <p><b>【ステップ3】選択肢の整理</b></p>
               <ul>
                 <li>B ⇒ DNMT3L/DNMT3A は<b>新規</b>メチル化の組み合わせです。</li>
                 <li>C ⇒ TET は<b>脱</b>メチル化の酵素です。</li>
                 <li>D ⇒ MBD はメチル化CpGに結合して抑制因子を呼ぶタンパク質で、DNAを切断しません。</li>
               </ul>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg14", ...CAT,
    title: "能動的脱メチル化 ─ TET から塩基除去修復へ", diff: 290, tags: ["脱メチル化", "TET", "5hmC", "BER"],
    explain: `<p>哺乳類の<b>能動的</b>DNA脱メチル化は、TET酵素による酸化から始まる。</p>
              <p>5-メチルシトシン（5mC）が最終的に非修飾のシトシンへ戻るまでの経路として、正しい順序はどれか。</p>`,
    type: "radio",
    options: [
      "5mC → 5hmC → 5fC → 5caC → TDG による塩基除去 → 塩基除去修復(BER) → C",
      "5mC → 5caC → 5fC → 5hmC → TDG による塩基除去 → ヌクレオチド除去修復(NER) → C",
      "5mC → 5hmC → TDG による塩基除去 → ミスマッチ修復(MMR) → C",
      "5mC → 脱アミノ化により T → ミスマッチ修復(MMR) → C",
    ],
    answer: 0,
    hints: [
      "TET は酸化酵素です。メチル基 CH₃ が段階的に酸化されていくと、側鎖はどう変わるでしょうか。",
      "最後は「塩基を1つ抜いて入れ直す」修復系が使われます。",
    ],
    solution: `<p><b>【ステップ1】側鎖の酸化を化学式で追う</b></p>
               <p>TET（TET1/2/3、Fe(II)と2-オキソグルタル酸を使うジオキシゲナーゼ）が
               シトシン5位の置換基を<b>段階的に酸化</b>していきます。</p>
               <table>
                 <tr><th>名称</th><th>側鎖 R</th><th>段階</th></tr>
                 <tr><td>5mC（5-メチルシトシン）</td><td>CH₃</td><td>出発点</td></tr>
                 <tr><td><b>5hmC</b>（ヒドロキシメチル）</td><td><b>CH₂OH</b></td><td>1回目の酸化</td></tr>
                 <tr><td><b>5fC</b>（ホルミル）</td><td><b>CHO</b></td><td>2回目の酸化</td></tr>
                 <tr><td><b>5caC</b>（カルボキシル）</td><td><b>COOH</b></td><td>3回目の酸化</td></tr>
               </table>
               <p>CH₃ → CH₂OH → CHO → COOH という、有機化学でおなじみの酸化の階段です。
               ここは化学式で書かせる出題があるので、順番と式をセットで覚えてください。</p>
               <p><b>【ステップ2】塩基を抜いて入れ直す</b></p>
               <p>5fC と 5caC は <b>TDG（チミンDNAグリコシラーゼ）</b>に認識され、
               塩基とデオキシリボースの間のグリコシド結合が切られます（脱塩基部位＝APサイトができる）。
               そこへ <b>塩基除去修復（BER：base excision repair）</b>が働き、
               APエンドヌクレアーゼ → DNAポリメラーゼ → リガーゼの順に<b>非修飾のシトシン</b>が入ります。</p>
               <p><b>【ステップ3】受動的脱メチル化との対比</b></p>
               <p>一方の<b>受動的</b>脱メチル化は酵素で消すのではなく、
               UHRF1/DNMT1 が働けない状況で<b>複製のたびに希釈されて薄まる</b>という消え方です。
               5hmC になると UHRF1 が認識できなくなるため、TET は受動的脱メチル化の引き金にもなります。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg15", ...CAT,
    title: "DNAメチル化とヒストン修飾の連動", diff: 285, tags: ["エピジェネティクス", "連動モデル", "HP1"],
    explain: `<p>ヘテロクロマチンでは、DNAメチル化とヒストン修飾が互いを呼び込み合って、抑制状態が安定に保たれる。</p>
              <p>この<b>連動モデル</b>の説明として、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "メチル化DNAに結合したタンパク質がH3K9メチル化酵素を呼び、生じたH3K9meにHP1が結合し、HP1がさらに隣のヌクレオソームへ修飾を広げるとともにDNAメチル化酵素も呼び込む。",
      "DNAメチル化とヒストン修飾は独立に決まり、互いに影響しない。",
      "H3K4メチル化がDNAメチル化酵素を呼び込み、抑制状態を作る。",
      "DNAメチル化はヒストンを完全に排除することで転写を止める。",
    ],
    answer: 0,
    hints: [
      "「読み取る → 書き込む → 隣へ伝える」という輪ができていると考えてください。",
      "HP1 はクロモドメインで H3K9me に結合します。結合したあと何をするかが鍵です。",
    ],
    solution: `<p><b>【ステップ1】輪をひとつずつたどる</b></p>
               <ol>
                 <li><b>DNAメチル化</b>（5mC）に <b>MBDタンパク質</b>（MeCP2 など）が結合する</li>
                 <li>MBD が <b>H3K9メチル化酵素</b>（SUV39H1 など）を呼び込む</li>
                 <li>できた <b>H3K9me2/3</b> に <b>HP1</b> が<b>クロモドメイン</b>で結合する</li>
                 <li>HP1 は<b>さらにH3K9メチル化酵素を呼ぶ</b> → 隣のヌクレオソームにも H3K9me が広がる（<b>伝播</b>）</li>
                 <li>HP1 は <b>DNMT</b> も呼び込む → 隣の領域の DNA もメチル化される</li>
               </ol>
               <p>つまり「DNAメチル化 → ヒストン修飾 → DNAメチル化」という<b>正のフィードバックの輪</b>が回り、
               抑制状態が自己増殖的に維持・拡大します。これが記述問題の中心です。</p>
               <p><b>【ステップ2】なぜ「連動」が重要か</b></p>
               <p>DNAメチル化は複製を越えて受け継がれますが（DNMT1/UHRF1）、
               ヒストン修飾は複製で半分に薄まります。連動していることで、
               <b>片方が薄れてももう片方から書き直せる</b>ため、細胞分裂を越えて記憶が保たれます。</p>
               <p><b>【ステップ3】読み取りドメインの整理</b></p>
               <table>
                 <tr><th>ドメイン</th><th>読む修飾</th><th>もつタンパク質の例</th></tr>
                 <tr><td><b>クロモドメイン</b></td><td>メチル化ヒストン</td><td>HP1</td></tr>
                 <tr><td><b>ブロモドメイン</b></td><td>アセチル化ヒストン</td><td>p300</td></tr>
                 <tr><td><b>CXXCドメイン</b></td><td><b>非</b>メチル化CpG</td><td>CFP1, MLL1/2</td></tr>
               </table>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg16", ...CAT,
    title: "クロマチン再構成複合体のはたらき", diff: 205, tags: ["SWI/SNF", "ヌクレオソーム", "ATP"],
    explain: `<p>SWI/SNF や ISWI、CHD といった<b>クロマチン再構成（リモデリング）複合体</b>について、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "ATPの加水分解エネルギーを使い、ヌクレオソームのスライド・排除・ヒストンの交換を行う。",
      "ヒストンにアセチル基を付加することでヌクレオソームをゆるめる。",
      "DNAを切断してヌクレオソームを取り除く。",
      "ヌクレオソームを新しく作るための、ヒストンの合成酵素である。",
    ],
    answer: 0,
    hints: [
      "「再構成」複合体は、修飾を書き込むのではなく、物理的に動かす装置です。",
      "動かすにはエネルギーが要ります。",
    ],
    solution: `<p><b>【ステップ1】修飾酵素と再構成複合体は別物</b></p>
               <table>
                 <tr><th></th><th>ヒストン修飾酵素</th><th>クロマチン再構成複合体</th></tr>
                 <tr><td>例</td><td>HAT（p300）, HDAC, SUV39H1</td><td><b>SWI/SNF, ISWI, CHD, INO80</b></td></tr>
                 <tr><td>すること</td><td>化学基を付け外しする</td><td><b>ヌクレオソームを物理的に動かす</b></td></tr>
                 <tr><td>エネルギー</td><td>アセチルCoA・SAM など</td><td><b>ATP の加水分解</b></td></tr>
               </table>
               <p><b>【ステップ2】3つの動かし方</b></p>
               <ul>
                 <li><b>スライド</b>：ヌクレオソームをDNA上で移動させ、転写因子の結合部位を露出させる</li>
                 <li><b>排除（放出）</b>：ヌクレオソームそのものを外す</li>
                 <li><b>交換</b>：コアヒストンを<b>ヒストンバリアント</b>（H2A.Z, H3.3 など）に置き換える</li>
               </ul>
               <p><b>【ステップ3】結果として何が見えるか</b></p>
               <p>活発に転写される遺伝子では、<b>転写開始点の上流でヌクレオソームが失われ</b>
               （NDR：ヌクレオソーム除去領域）、<b>下流では位相がそろって規則正しく並ぶ</b>という
               特徴的な配置が観察されます。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg17", ...CAT,
    title: "ポリコーム複合体 PRC1 と PRC2", diff: 275, tags: ["ポリコーム", "PRC1", "PRC2", "H3K27me3"],
    explain: `<p>ポリコーム群タンパク質は、発生の過程で遺伝子を長期的に抑制する。</p>
              <p>PRC1 と PRC2 が入れる修飾の組み合わせとして正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "PRC1 → H2AK119 のユビキチン化、PRC2 → H3K27 のメチル化",
      "PRC1 → H3K27 のメチル化、PRC2 → H2AK119 のユビキチン化",
      "PRC1 → H3K9 のメチル化、PRC2 → H3K4 のメチル化",
      "PRC1 → H3K27 のアセチル化、PRC2 → H3K27 の脱アセチル化",
    ],
    answer: 0,
    hints: [
      "番号の大きい PRC2 が、番号の大きいリシン K27 のメチル化…とは限りません。素直に対応を覚えます。",
      "PRC1 はユビキチンリガーゼ活性を持つ複合体です。",
    ],
    solution: `<p><b>【ステップ1】対応を覚える</b></p>
               <table>
                 <tr><th>複合体</th><th>酵素活性</th><th>入れる修飾</th></tr>
                 <tr><td><b>PRC2</b></td><td>メチル基転移酵素（EZH2）</td><td><b>H3K27me1/2/3</b></td></tr>
                 <tr><td><b>PRC1</b></td><td>ユビキチンリガーゼ（RING1）</td><td><b>H2AK119ub</b></td></tr>
               </table>
               <p>古典的なモデルでは <b>PRC2 が先に H3K27me3 を書き、それを PRC1 が読んで H2AK119ub を入れ、
               クロマチンを凝縮させる</b>という順に働きます（非古典的な経路もあります）。</p>
               <p><b>【ステップ2】ヘテロクロマチンの2つの型</b></p>
               <table>
                 <tr><th></th><th>構成的ヘテロクロマチン</th><th>通性ヘテロクロマチン</th></tr>
                 <tr><td>修飾</td><td><b>H3K9me3</b></td><td><b>H3K27me3</b></td></tr>
                 <tr><td>読み手</td><td>HP1</td><td>PRC1</td></tr>
                 <tr><td>場所</td><td>セントロメア・テロメアなど常に不活性</td><td>細胞種によって変わる（Hox遺伝子など）</td></tr>
               </table>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg18", ...CAT,
    title: "位置効果と酵母のコロニー実験", diff: 280, tags: ["位置効果", "テロメア", "Sir2"],
    explain: `<p>酵母の <b>ADE2</b> 遺伝子は、働いていれば白いコロニー、働かなければ赤いコロニーになる。</p>
              <p>この ADE2 遺伝子を<b>テロメア近傍</b>へ移すと、白と赤がまだらに混じったコロニーが現れる。この観察が示すこととして最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "遺伝子の発現は塩基配列だけでなく、置かれたクロマチン環境によって決まり、その抑制状態は可逆的で、細胞分裂を越えて娘細胞に受け継がれる。",
      "テロメア近傍へ移すと ADE2 遺伝子に変異が入り、機能が失われる。",
      "テロメア近傍では ADE2 遺伝子が必ず完全に発現しなくなる。",
      "コロニーの色は培地の成分だけで決まり、遺伝子の位置とは無関係である。",
    ],
    answer: 0,
    hints: [
      "「まだら」であることに意味があります。全部赤でも全部白でもない、という点です。",
      "赤い部分と白い部分は、それぞれ1個の細胞から増えた集団です。",
    ],
    solution: `<p><b>【ステップ1】まだらであることの意味</b></p>
               <p>配列が壊れているなら<b>全部赤</b>になるはずです。まだらになるということは、</p>
               <ul>
                 <li>同じ配列でも<b>発現する細胞と抑制される細胞がある</b>（＝配列以外の要因で決まる）</li>
                 <li>いったん決まった状態は、その細胞から増えた集団で<b>維持される</b>（＝分裂を越えて継承される）</li>
                 <li>ときどき切り替わる（＝<b>可逆的</b>）</li>
               </ul>
               <p>この3点がまさに<b>エピジェネティック制御</b>の定義です。「配列を変えずに、
               受け継がれる発現状態の違いを作る」ことを示した古典的実験になります。</p>
               <p><b>【ステップ2】仕組み</b></p>
               <p>テロメアからヘテロクロマチンが内側へ<b>伝播</b>し、届いた細胞では ADE2 が沈黙します
               （<b>テロメア位置効果</b>）。酵母では <b>Sir2</b>（NAD⁺依存性ヒストン脱アセチル化酵素）を
               含む Sir 複合体がこの伝播を担い、Sir2 を失うと抑制が解けます。
               伝播がどこで止まるかは確率的なので、細胞ごとに運命が分かれてまだらになります。</p>
               <p><b>【ステップ3】伝播を止めるもの</b></p>
               <p>ヘテロクロマチンの際限ない広がりは<b>インスレーター</b>が食い止めます。
               インスレーターには (1) エンハンサーの働きを遮断する、
               (2) ヘテロクロマチンの伝播をブロックする、という2つの機能があります。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg19", ...CAT,
    title: "HP1 とヘテロクロマチンの液-液相分離", diff: 300, tags: ["HP1", "相分離", "クロモドメイン"],
    explain: `<p>HP1（heterochromatin protein 1）がヘテロクロマチンを形成する仕組みについて、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "クロモドメインで H3K9me に結合し、二量体からオリゴマーへと集まり、高濃度になると液-液相分離を起こして周囲と区切られた領域を作る。",
      "ブロモドメインで H3K9ac に結合し、ヌクレオソームを能動的に排除する。",
      "DNAを直接切断してループを作り、遺伝子を物理的に隔離する。",
      "H3K4me3 に結合し、転写装置を集めて発現を高める。",
    ],
    answer: 0,
    hints: [
      "HP1 が読むのはメチル化ヒストンです。読み取りドメインの名前を思い出してください。",
      "近年は「膜のない区画」がどうできるか、という文脈で語られます。",
    ],
    solution: `<p><b>【ステップ1】結合の仕方</b></p>
               <p>HP1 は<b>クロモドメイン</b>で <b>H3K9me2/3</b> を認識します
               （アセチル化を読むのはブロモドメイン、という対応を必ずセットで覚えます）。</p>
               <p><b>【ステップ2】集まり方</b></p>
               <p>HP1 は<b>二量体</b>を作り、さらに互いに弱く多点で結合してオリゴマー化します。
               この「弱い相互作用が多数」という状況が、<b>液-液相分離（LLPS）</b>の典型的な条件です。
               濃度が十分に高まると、HP1 とクロマチンが液滴のようにまとまり、
               <b>膜がないのに周囲と性質の異なる区画</b>ができます。</p>
               <p><b>【ステップ3】何が嬉しいのか</b></p>
               <p>相分離した領域には転写装置が入りにくくなるため、抑制が物理的に強化されます。
               また液体的な性質のおかげで、必要なときには速やかに溶けて解除できます。
               「固く固まった塊」ではなく「濃く集まった液滴」というイメージが要点です。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg20", ...CAT,
    title: "非メチル化CpGを読む CXXC ドメイン", diff: 295, tags: ["CXXC", "CpGアイランド", "H3K4"],
    explain: `<p>CpGアイランドの多くは非メチル化状態に保たれている。</p>
              <p>この状態を守る仕組みとして正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "CXXCドメインをもつ CFP1 や MLL1/2 が非メチル化CpGに結合し、H3K4をメチル化する。H3K4メチル化は DNMT3A/3L の結合を妨げるため、新規メチル化が入らない。",
      "CXXCドメインをもつタンパク質がメチル化CpGに結合し、脱メチル化酵素を呼び込む。",
      "CpGアイランドではDNMT1が働かないため、複製のたびにメチル化が失われる。",
      "CpGアイランドはヌクレオソームを持たないため、メチル化酵素が近づけない。",
    ],
    answer: 0,
    hints: [
      "「非メチル化を読むドメイン」があります。読んだあと、どんな修飾を書き込むでしょうか。",
      "reg13 で扱った DNMT3L の性質（どんなヒストンテールを好むか）を思い出してください。",
    ],
    solution: `<p><b>【ステップ1】非メチル化を読むドメイン</b></p>
               <p><b>CXXCドメイン</b>は<b>非メチル化CpG</b>に特異的に結合します。
               メチル化CpGを読む MBD ドメインとちょうど逆の役割です。</p>
               <p><b>【ステップ2】守りの輪</b></p>
               <ol>
                 <li>CXXC をもつ <b>CFP1 / MLL1/2</b> が非メチル化CpGアイランドに結合</li>
                 <li>これらが <b>H3K4 メチル化酵素</b>を伴い、<b>H3K4me3</b> を書き込む</li>
                 <li><b>DNMT3L は非修飾のH3K4テールにしか結合できない</b>ため、H3K4me3 があると DNMT3A/3L が来られない</li>
                 <li>結果として<b>新規メチル化が入らず、非メチル化が維持される</b></li>
               </ol>
               <p>reg15 の「抑制の輪」と対になる、<b>活性状態を守る輪</b>です。
               エピジェネティクスでは、抑制側と活性側の両方に自己維持のループがあり、
               互いを排除し合っていると理解すると全体が見通せます。</p>
               <p><b>【ステップ3】破綻するとどうなるか</b></p>
               <p>がん細胞ではこの守りが破れ、本来非メチル化のはずの
               <b>がん抑制遺伝子のCpGアイランドが高メチル化</b>して沈黙することが知られています
               （一方でゲノム全体は低メチル化に傾きます）。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg21", ...CAT,
    title: "インスレーターと CTCF", diff: 245, tags: ["インスレーター", "CTCF", "エンハンサー"],
    explain: `<p>ゲノム上の<b>インスレーター</b>（絶縁配列）がもつ機能として、正しい組み合わせはどれか。</p>`,
    type: "radio",
    options: [
      "エンハンサーがプロモーターに働きかけるのを遮断する機能と、ヘテロクロマチンの伝播をブロックする機能",
      "転写を直接活性化する機能と、mRNAを安定化する機能",
      "DNA複製の開始点として働く機能と、姉妹染色分体を接着する機能",
      "リボソームを呼び込む機能と、翻訳を終結させる機能",
    ],
    answer: 0,
    hints: [
      "「絶縁体」という名前のとおり、何かが伝わるのを止める配列です。",
      "止める対象は2種類あります。",
    ],
    solution: `<p><b>【ステップ1】2つの遮断</b></p>
               <ul>
                 <li><b>エンハンサーブロッキング</b>：エンハンサーとプロモーターの<b>間</b>に置かれたときだけ、
                 その作用を遮る。外側に置いても効果はない（＝位置依存的）。</li>
                 <li><b>バリア機能</b>：ヘテロクロマチンが隣の領域へ広がってくるのを食い止める。</li>
               </ul>
               <p><b>【ステップ2】担い手は CTCF</b></p>
               <p>脊椎動物のインスレーターの多くには <b>CTCF</b> が結合します。
               CTCF は<b>コヒーシン</b>と協調して DNA のループを作り、
               <b>TAD（Topologically Associating Domain）</b>の境界を規定します。</p>
               <p><b>【ステップ3】TAD が壊れると</b></p>
               <p>境界の欠失や逆位で TAD が融合すると、
               <b>本来は別のドメインにあったエンハンサーが隣の遺伝子を誤って活性化</b>します。
               PAX3 や WNT6 の異所的な発現による四肢の形態異常が有名な例で、
               「エンハンサー・ハイジャック」と呼ばれます。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "reg22", ...CAT,
    title: "活性型エンハンサーの目印", diff: 235, tags: ["エンハンサー", "H3K4me1", "H3K27ac"],
    explain: `<p>ChIP-seq のデータから<b>活性化しているエンハンサー</b>を見分けたい。</p>
              <p>目印となるヒストン修飾の組み合わせとして最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "H3K4me1 と H3K27ac がともに豊富",
      "H3K4me3 が豊富で H3K27ac は乏しい",
      "H3K9me3 と H3K27me3 がともに豊富",
      "H4K20me3 が豊富で H3K4me1 は乏しい",
    ],
    answer: 0,
    hints: [
      "プロモーターの目印（H3K4me3）と区別できる修飾が必要です。",
      "「準備できている」と「実際に働いている」の違いを付けるのがアセチル化です。",
    ],
    solution: `<p><b>【ステップ1】プロモーターとエンハンサーの区別</b></p>
               <table>
                 <tr><th></th><th>H3K4me3</th><th>H3K4me1</th><th>H3K27ac</th></tr>
                 <tr><td><b>活性プロモーター</b></td><td>高</td><td>低</td><td>高</td></tr>
                 <tr><td><b>活性エンハンサー</b></td><td>低</td><td><b>高</b></td><td><b>高</b></td></tr>
                 <tr><td>待機（poised）エンハンサー</td><td>低</td><td><b>高</b></td><td><b>低</b></td></tr>
               </table>
               <p><b>K4 のメチル基の数</b>でプロモーター（me3）とエンハンサー（me1）を分け、
               <b>K27ac の有無</b>で「今working しているか」を判定するのが基本の読み方です。</p>
               <p><b>【ステップ2】インスレーターの判定</b></p>
               <p>図から領域の種類を答えさせる問題では、<b>CTCF のピークが立っていればインスレーター</b>
               を疑います。ヒストン修飾が乏しく CTCF だけが強い、という見え方をします。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },
];
